import React, {
	createContext,
	ReactElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState
} from 'react';

import { Global, css, Theme as EmotionTheme } from '@emotion/react';
import {
	generateColorSet,
	ThemeProvider as UIThemeProvider,
	ThemeProviderProps as UIThemeProviderProps
} from '@arens/aren-design-system';
import { auto, disable, enable, setFetchMethod } from 'darkreader';

import { getAutoScalingFontSize } from './utils';
import { DarkReaderPropValues, ThemeExtension } from '../../types';
import { darkReaderDynamicThemeFixes } from '../constants';
import { useGetPrimaryColor } from '../primary-color/use-get-primary-color';

declare module '@emotion/react' {
	export interface Theme {
		palette: Palette;
		icons?: Record<string, unknown>;
		[key: string]: unknown;
	}
}

type ColorSet = {
	regular: string;
	hover?: string;
	active?: string;
	focus?: string;
	disabled?: string;
};

type Palette = {
	shared: Required<ColorSet>;
	linked: Required<ColorSet>;
	primary?: ColorSet;
	[key: string]: unknown;
};

setFetchMethod(window.fetch);

interface ThemeCallbacks {
	addExtension: (newExtension: ThemeExtension, id: string) => void;
	setDarkReaderState: (newState: DarkReaderPropValues) => void;
}

export const ThemeCallbacksContext = createContext<ThemeCallbacks>({
	addExtension: () => {
		throw Error('Not implemented');
	},
	setDarkReaderState: () => {
		throw Error('not implemented');
	}
});

type CustomTheme = Partial<Omit<EmotionTheme, 'palette'>> & {
	palette?: Partial<EmotionTheme['palette']>;
};

const paletteExtension =
	(customTheme: CustomTheme = {}) =>
	(theme: EmotionTheme): EmotionTheme => ({
		...theme,
		...customTheme,
		palette: {
			...theme.palette,
			...customTheme.palette,
			shared: {
				regular: '#FFB74D',
				hover: '#FFA21A',
				active: '#FFA21A',
				focus: '#FF9800',
				disabled: '#FFD699'
			},
			linked: {
				regular: '#AB47BC',
				hover: '#8B3899',
				active: '#8B3899',
				focus: '#7A3187',
				disabled: '#DDB4E4'
			},
			...((): Record<string, ColorSet> => {
				if (customTheme.palette?.primary) {
					return { primary: customTheme.palette.primary };
				}
				if (theme.palette?.primary) {
					return { primary: theme.palette.primary };
				}
				return {};
			})()
		}
	});

const iconExtension: ThemeExtension = (theme) => ({
	...theme,
	icons: {
		...theme.icons,
		Shared: theme.icons?.ArrowCircleRight,
		Linked: theme.icons?.ArrowCircleLeft
	}
});

const fontExtension: ThemeExtension = (theme) => ({
	...theme,
	fonts: {
		...theme.fonts,
		default: "'Vazirmatn Variable', 'Roboto', sans-serif"
	}
});

interface GlobalStyledProps {
	baseFontSize: number;
}

const GlobalStyle = ({ baseFontSize }: GlobalStyledProps): ReactElement => (
	<Global
		styles={css`
			html {
				font-size: ${baseFontSize}%;
			}
		`}
	/>
);

interface ThemeProviderProps {
	children?: React.ReactNode | React.ReactNode[];
}

export const ThemeProvider = ({ children }: ThemeProviderProps): JSX.Element => {
	const [extensions, setExtensions] = useState<Partial<Record<string, ThemeExtension>>>({});
	const primaryColor = useGetPrimaryColor();

	useLayoutEffect(() => {
		const customThemePalette: Partial<EmotionTheme['palette']> = primaryColor
			? { primary: generateColorSet({ regular: primaryColor }) }
			: {};

		setExtensions((extension) => ({
			...extension,
			palette: paletteExtension({
				palette: customThemePalette
			}),
			icons: iconExtension,
			fonts: fontExtension
		}));
	}, [primaryColor]);

	const [darkReaderState, setDarkReaderState] = useState<DarkReaderPropValues>('disabled');

	useEffect(() => {
		switch (darkReaderState) {
			case 'disabled':
				auto(false);
				disable();
				break;
			case 'enabled':
				auto(false);
				enable(
					{
						sepia: -50
					},
					darkReaderDynamicThemeFixes
				);
				break;
			case 'auto':
			default:
				auto(
					{
						sepia: -50
					},
					darkReaderDynamicThemeFixes
				);
				break;
		}
	}, [darkReaderState]);

	const aggregatedExtensions = useCallback<NonNullable<UIThemeProviderProps['extension']>>(
		(theme) => {
			let themeAccumulator = theme;

			Object.values(extensions).forEach((themeExtensionFn) => {
				if (themeExtensionFn) {
					themeAccumulator = themeExtensionFn(
						themeAccumulator as unknown as EmotionTheme
					) as unknown as typeof themeAccumulator;
				}
			});

			return themeAccumulator;
		},
		[extensions]
	);

	const addExtension = useCallback<ThemeCallbacks['addExtension']>((newExtension, id) => {
		setExtensions((ext) => ({ ...ext, [id]: newExtension }));
	}, []);

	const baseFontSize = useMemo<GlobalStyledProps['baseFontSize']>(() => {
		return getAutoScalingFontSize();
	}, []);

	return (
		<UIThemeProvider extension={aggregatedExtensions}>
			<ThemeCallbacksContext.Provider value={{ addExtension, setDarkReaderState }}>
				<GlobalStyle baseFontSize={baseFontSize} />
				{children}
			</ThemeCallbacksContext.Provider>
		</UIThemeProvider>
	);
};
